import { DrawerCard, DrawerField, Tooltip } from "@mixitone/components";
import { ApplicationView, useController } from "@mixitone/mvc";
import Form from "../Form";
import FormButtons from "../FormButtons";
import { FormController } from "../FormController";
import type { CheckboxField, FormFieldConfig } from "../FormFieldsDefinition";
import { ValueComponent } from "../FormValueComponents";

interface DrawerFormRendererProps {
  name: string;
  fields: FormFieldConfig[];
  showDelete?: boolean;
}

export const DrawerFormField = ApplicationView(
  ({
    field,
    record,
    autoFocus,
    hideLabel,
  }: {
    field: FormFieldConfig;
    record: any;
    autoFocus?: boolean;
    hideLabel?: boolean;
  }) => {
    const variant =
      field.type === "checkbox" ? ((field as CheckboxField).buttonVariant ? "button" : "toggle") : undefined;
    if (field.condition && !field.condition(record)) return null;

    if (hideLabel || field.hideLabel) {
      return <ValueComponent field={field} record={record} autoFocus={autoFocus} variant={variant} />;
    }

    return (
      <DrawerField label={field.label} autoFocus={autoFocus} helpText={field.helpText} htmlFor={field.name}>
        <ValueComponent field={field} record={record} autoFocus={autoFocus} variant={variant} />
        {field.description && <Tooltip icon>{field.description}</Tooltip>}
      </DrawerField>
    );
  },
);

export const DrawerFormFieldset = ApplicationView(({ fields }: { fields: FormFieldConfig[] }) => {
  const controller = useController(FormController);
  const { record, saving } = controller.state;

  return (
    <fieldset disabled={saving}>
      <DrawerCard>
        {fields.map((field) => (
          <DrawerFormField key={field.name} field={field} record={record} hideLabel={field.hideLabel} />
        ))}
      </DrawerCard>
    </fieldset>
  );
});

const DrawerFormRenderer: React.FC<DrawerFormRendererProps> = ({ name, fields, showDelete = true }) => {
  return (
    <Form name={name}>
      <div className="flex flex-col gap-4">
        <DrawerFormFieldset fields={fields} />
        <DrawerCard>
          <FormButtons showDelete={showDelete} />
        </DrawerCard>
      </div>
    </Form>
  );
};

export default ApplicationView(DrawerFormRenderer);
